import * as React from 'react'

import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { ArchiveIcon } from '~/client/src/shared/components/icons/ArchiveIcon'
import { ArrowBackIcon } from '~/client/src/shared/components/icons/ArrowBackIcon'
import { CheckDoneIcon } from '~/client/src/shared/components/icons/CheckDoneIcon'
import { ClearDayIcon } from '~/client/src/shared/components/icons/ClearDayIcon'
import { EmailIcon } from '~/client/src/shared/components/icons/EmailIcon'
import { UnarchiveIcon } from '~/client/src/shared/components/icons/UnarchiveIcon'
import { UnreadIcon } from '~/client/src/shared/components/icons/UnreadIcon'
import { UserFilledIcon } from '~/client/src/shared/components/icons/UserFilledIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import StatusUpdateMenu from '~/client/src/shared/components/StatusUpdateMenu/StatusUpdateMenu'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'
import { toggleAllFilteredSelection } from '~/client/src/shared/lists/toggleAllFilteredSelection'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BaseNotification from '~/client/src/shared/models/Notification'
import SitePermit from '~/client/src/shared/models/Permit'
import PermitTypesStore from '~/client/src/shared/stores/domain/PermitTypes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import {
  archive,
  BulkActionsDeps,
  bulkApprove,
  canMarkAsDone,
  confirmStatusUpdate,
  getApprovableUsers,
  getCommonStepItemsForSelected,
  getIsAdmin,
  getSelectedPermits,
  hasPermitNotificationsSelected,
  hasReadSelected,
  hasUnreadSelected,
  isAllSelected,
  isSomeSelected,
  markAsRead,
  markAsUnread,
  unarchive,
} from '~/client/src/shared/utils/notificationBulkActions'
import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'
import { IStatusItem } from '~/client/src/shared/utils/permitStatusUtils'

import MobileEventStore from '../../../stores/EventStore/MobileEvents.store'

interface IProps {
  notificationsListStore: NotificationsListStore
  filteredNotifications: BaseNotification[]
  onExitMultiSelect: () => void
  permitTypesStore?: PermitTypesStore
  eventsStore?: MobileEventStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
}

@inject(
  'eventsStore',
  'permitTypesStore',
  'projectMembersStore',
  'userProjectsStore',
)
@observer
export default class MobileBulkActionsBar extends React.Component<IProps> {
  @observable private pendingItem: IStatusItem | null = null
  @observable private pendingPermits: SitePermit[] = []
  @observable private isConfirmDialogOpen: boolean = false
  @observable private isStatusDrawerOpen: boolean = false
  private closeOverlay: (() => void) | null = null

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidUpdate() {
    if (this.isStatusDrawerOpen && !this.closeOverlay) {
      this.closeOverlay = notifyOverlayOpen()
    } else if (!this.isStatusDrawerOpen && this.closeOverlay) {
      this.closeOverlay()
      this.closeOverlay = null
    }
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
  }

  public render() {
    const { notificationsListStore } = this.props
    const selectedNotifications = this.selectedNotifications
    const count = notificationsListStore.list.selection.count
    const allSelected = isAllSelected(
      this.props.filteredNotifications,
      selectedNotifications,
    )
    const someSelected = isSomeSelected(
      this.props.filteredNotifications,
      selectedNotifications,
    )
    const adminWithPermits =
      getIsAdmin(this.props.eventsStore) &&
      hasPermitNotificationsSelected(selectedNotifications)

    return (
      <>
        {/* Row 1: Back arrow + pill buttons */}
        <div className="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:border-b tw:border-border">
          <button
            className="tw:flex tw:items-center tw:justify-center tw:w-9 tw:h-9 tw:shrink-0 tw:bg-transparent tw:border-none tw:cursor-pointer tw:text-foreground tw:rounded-full tw:active:bg-muted tw:p-0"
            onClick={this.props.onExitMultiSelect}
          >
            <ArrowBackIcon size="md" />
          </button>

          <div className="tw:flex tw:gap-2 tw:overflow-x-auto tw:flex-1 tw:[&::-webkit-scrollbar]:hidden tw:[scrollbar-width:none]">
            <Button
              variant="primary-outline"
              disabled={!hasUnreadSelected(selectedNotifications)}
              className="tw:whitespace-nowrap"
              onClick={this.handleMarkAsRead}
            >
              <EmailIcon size="xs" data-icon="inline-start" />
              {Localization.translator.read}
            </Button>

            <Button
              variant="primary-outline"
              disabled={!hasReadSelected(selectedNotifications)}
              className="tw:whitespace-nowrap"
              onClick={this.handleMarkAsUnread}
            >
              <UnreadIcon size="xs" data-icon="inline-start" />
              {Localization.translator.unread}
            </Button>

            <Button
              variant="primary-outline"
              disabled={
                !(adminWithPermits && canMarkAsDone(selectedNotifications))
              }
              className="tw:whitespace-nowrap"
              onClick={this.handleDone}
            >
              <CheckDoneIcon size="sm" data-icon="inline-start" />
              {Localization.translator.done}
            </Button>

            <Button
              variant="primary-outline"
              disabled={!adminWithPermits}
              className="tw:whitespace-nowrap"
              onClick={this.openStatusDrawer}
            >
              <ClearDayIcon size="sm" data-icon="inline-start" />
              {Localization.translator.updateStatus}
            </Button>

            <Button
              variant="primary-outline"
              disabled={
                !(
                  getApprovableUsers(
                    selectedNotifications,
                    this.props.projectMembersStore,
                    this.props.userProjectsStore,
                  ).length > 0
                )
              }
              className="tw:whitespace-nowrap"
              onClick={this.handleBulkApprove}
            >
              <UserFilledIcon
                size="sm"
                className="tw:text-blue-600"
                data-icon="inline-start"
              />
              {Localization.translator.approve_verb}
            </Button>

            {notificationsListStore.isArchiveFilterActive ? (
              <Button
                variant="primary-outline"
                className="tw:whitespace-nowrap"
                onClick={this.handleUnarchive}
              >
                <UnarchiveIcon size="xs" data-icon="inline-start" />
                {Localization.translator.unArchive}
              </Button>
            ) : (
              <Button
                variant="primary-outline"
                className="tw:whitespace-nowrap"
                onClick={this.handleArchive}
              >
                <ArchiveIcon size="xs" data-icon="inline-start" />
                {Localization.translator.archive}
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: Select-all checkbox + counter */}
        <div className="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-1.5 tw:border-b tw:border-border/40">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onCheckedChange={() => this.handleToggleSelectAll()}
          />
          <span className="tw:text-sm tw:text-muted-foreground">
            {count} {Localization.translator.selected.toLowerCase()}
          </span>
        </div>

        <BaseDialog.Root
          open={this.isStatusDrawerOpen}
          onOpenChange={nextOpen => {
            if (!nextOpen) this.closeStatusDrawer()
          }}
        >
          <BaseDialog.Portal>
            <BaseDialog.Backdrop className="tw:fixed tw:inset-0 tw:bg-black/30 tw:z-50" />
            <BaseDialog.Popup className="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-50 tw:bg-background tw:rounded-t-2xl tw:shadow-lg tw:max-h-[70vh] tw:flex tw:flex-col tw:pb-safe-3">
              <div className="tw:w-10 tw:h-1 tw:bg-border tw:rounded-full tw:mx-auto tw:mt-2 tw:mb-1" />
              <StatusUpdateMenu
                items={getCommonStepItemsForSelected(
                  selectedNotifications,
                  this.props.permitTypesStore,
                )}
                title={Localization.translator.updateSelectedWorkflowsStatusTo}
                onStatusSelected={this.handleStatusSelected}
                onClose={this.closeStatusDrawer}
              />
            </BaseDialog.Popup>
          </BaseDialog.Portal>
        </BaseDialog.Root>

        <ConfirmDialog
          isOpen={this.isConfirmDialogOpen}
          onDoneClicked={this.handleConfirmStatusUpdate}
          onCancelClicked={this.handleCancelStatusUpdate}
          title={Localization.translator.updateStatus}
          question={`Update ${this.pendingPermits.length} workflow(s) status?`}
        >
          {this.pendingItem && (
            <div className="tw:flex tw:justify-center tw:py-2">
              <StatusBadge
                status={this.pendingItem.status}
                isLate={false}
                showDot={false}
                workflowStepLevel={this.pendingItem.workflowStepLevel}
              />
            </div>
          )}
        </ConfirmDialog>
      </>
    )
  }

  private get deps(): BulkActionsDeps {
    return {
      eventsStore: this.props.eventsStore,
      permitTypesStore: this.props.permitTypesStore,
      projectMembersStore: this.props.projectMembersStore,
      userProjectsStore: this.props.userProjectsStore,
      onAfterAction: this.props.onExitMultiSelect,
    }
  }

  private handleToggleSelectAll = () => {
    const list = this.props.notificationsListStore.list
    toggleAllFilteredSelection(list.selection, action => list.dispatch(action))
  }

  private handleMarkAsRead = () =>
    markAsRead(this.deps, this.selectedNotifications)
  private handleMarkAsUnread = () =>
    markAsUnread(this.deps, this.selectedNotifications)
  private handleArchive = () => archive(this.deps, this.selectedNotifications)
  private handleUnarchive = () =>
    unarchive(this.deps, this.selectedNotifications)
  private handleBulkApprove = () =>
    bulkApprove(this.deps, this.selectedNotifications)

  @action.bound
  private handleDone() {
    this.pendingPermits = getSelectedPermits(this.selectedNotifications)
    this.pendingItem = { status: SitePermitStatus.Done, workflowStepLevel: 0 }
    this.isConfirmDialogOpen = true
  }

  @action.bound
  private openStatusDrawer() {
    this.isStatusDrawerOpen = true
  }

  @action.bound
  private closeStatusDrawer() {
    this.isStatusDrawerOpen = false
  }

  @action.bound
  private handleStatusSelected(item: IStatusItem) {
    this.pendingPermits = getSelectedPermits(this.selectedNotifications)
    this.isStatusDrawerOpen = false
    this.pendingItem = item
    this.isConfirmDialogOpen = true
  }

  @action.bound
  private handleConfirmStatusUpdate() {
    confirmStatusUpdate(this.deps, this.pendingItem, this.pendingPermits)
    this.isConfirmDialogOpen = false
    this.pendingItem = null
    this.pendingPermits = []
  }

  @action.bound
  private handleCancelStatusUpdate() {
    this.isConfirmDialogOpen = false
    this.pendingItem = null
    this.pendingPermits = []
  }

  private get selectedNotifications(): BaseNotification[] {
    const { list } = this.props.notificationsListStore
    return list.inputRecords
      .filter(row => list.selection.selectedIds.has(row.id))
      .map(row => row.notification)
  }
}
